import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-amber-50 text-amber-700 border-amber-200",
  acknowledged: "bg-blue-50 text-blue-700 border-blue-200",
  resolved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-gray-100 text-gray-600 border-gray-200",
};

export default function AdminGrievanceTab() {
  const [officer, setOfficer] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grievances, setGrievances] = useState([]);
  const [open, setOpen] = useState(null); // grievance id
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [o, g] = await Promise.allSettled([
      api.get("/grievance/officer"),
      api.get("/admin/grievances"),
    ]);
    if (o.status === "fulfilled") { setOfficer(o.value.data); setForm(o.value.data); }
    if (g.status === "fulfilled") setGrievances(g.value.data || []);
  };

  useEffect(() => { load(); }, []);

  if (!form) return <div className="text-sm text-gray-500 py-10 text-center">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = ["name", "designation", "email", "phone", "address", "acknowledgement_hours", "resolution_days"]
    .some((k) => String(form[k] ?? "") !== String(officer?.[k] ?? ""));

  const save = async () => {
    if (!form.name?.trim() || !form.email?.trim() || !form.phone?.trim()) {
      return toast.error("Name, email and phone are required");
    }
    if (!form.email.includes("@")) return toast.error("Enter a valid email");
    if (Number(form.acknowledgement_hours) <= 0) return toast.error("Acknowledgement window must be positive");
    if (Number(form.resolution_days) <= 0) return toast.error("Resolution window must be positive");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        designation: (form.designation || "").trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: (form.address || "").trim(),
        acknowledgement_hours: Number(form.acknowledgement_hours),
        resolution_days: Number(form.resolution_days),
      };
      const { data } = await api.patch("/admin/grievance/officer", payload);
      setOfficer(data); setForm(data);
      toast.success("Grievance Officer details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const patchGrievance = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/grievances/${id}`, body);
      setGrievances((rows) => rows.map((r) => (r.id === id ? data : r)));
      toast.success("Grievance updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="space-y-8" data-testid="grievance-admin-tab">
      <div className="p-5 rounded-2xl bg-white border border-gray-200">
        <div className="mb-4">
          <div className="font-display text-2xl">Grievance Officer</div>
          <p className="text-xs text-gray-500 mt-1">These details appear on the public Grievance Redressal page at /legal/grievance. Save changes and they reflect immediately.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input data-testid="go-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Designation</Label>
            <Input data-testid="go-designation" value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="go-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="go-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Registered address</Label>
            <Input data-testid="go-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <Label>Acknowledgement window (hours)</Label>
            <Input data-testid="go-ack" type="number" min="1" value={form.acknowledgement_hours || 48} onChange={(e) => set("acknowledgement_hours", e.target.value)} />
          </div>
          <div>
            <Label>Resolution window (days)</Label>
            <Input data-testid="go-res" type="number" min="1" value={form.resolution_days || 30} onChange={(e) => set("resolution_days", e.target.value)} />
          </div>
        </div>
        <div className="mt-4">
          <Button data-testid="go-save" onClick={save} disabled={!dirty || busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
            {busy ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-baseline justify-between">
          <div className="font-display text-2xl">Grievances filed</div>
          <div className="text-xs text-gray-500">{grievances.length} total</div>
        </div>
        {grievances.length === 0 ? (
          <div className="text-sm text-gray-500 py-10 text-center rounded-2xl bg-white border border-gray-200">No grievances yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="w-full text-sm" data-testid="grievance-table">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="p-3 text-left">Ticket</th>
                  <th className="p-3 text-left">Buyer</th>
                  <th className="p-3 text-left">Order</th>
                  <th className="p-3 text-left">Filed</th>
                  <th className="p-3 text-left">Ack by</th>
                  <th className="p-3 text-left">Resolve by</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {grievances.map((g) => (
                  <tr key={g.id} data-testid={`grv-row-${g.id}`} className="border-t border-gray-100">
                    <td className="p-3 font-mono-tab text-xs">{g.ticket_no}</td>
                    <td className="p-3">
                      <div className="font-medium">{g.name}</div>
                      <div className="text-xs text-gray-500">{g.email}{g.phone ? ` · ${g.phone}` : ""}</div>
                    </td>
                    <td className="p-3 font-mono-tab text-xs">{g.order_id ? g.order_id.slice(0, 8).toUpperCase() : "—"}</td>
                    <td className="p-3 text-xs text-gray-600">{new Date(g.created_at).toLocaleDateString("en-IN")}</td>
                    <td className="p-3 text-xs text-gray-600">{new Date(g.acknowledge_by).toLocaleDateString("en-IN")}</td>
                    <td className="p-3 text-xs text-gray-600">{new Date(g.resolve_by).toLocaleDateString("en-IN")}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] uppercase tracking-widest ${STATUS_COLORS[g.status] || STATUS_COLORS.new}`}>
                        {g.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <Dialog open={open === g.id} onOpenChange={(v) => { setOpen(v ? g.id : null); if (v) setNotes(g.admin_notes || ""); }}>
                        <DialogTrigger asChild>
                          <Button data-testid={`grv-open-${g.id}`} variant="outline" className="rounded-full h-8">Review</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader><DialogTitle>Grievance {g.ticket_no}</DialogTitle></DialogHeader>
                          <div className="space-y-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500">From</div>
                              <div>{g.name} · {g.email}{g.phone ? ` · ${g.phone}` : ""}</div>
                            </div>
                            {g.order_id && (
                              <div>
                                <div className="text-xs text-gray-500">Order</div>
                                <div className="font-mono-tab text-xs">{g.order_id}</div>
                              </div>
                            )}
                            <div>
                              <div className="text-xs text-gray-500">Description</div>
                              <div className="whitespace-pre-wrap text-sm">{g.description}</div>
                            </div>
                            <div>
                              <Label>Internal notes</Label>
                              <Textarea data-testid={`grv-notes-${g.id}`} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">Status</Label>
                              <Select value={g.status} onValueChange={(v) => patchGrievance(g.id, { status: v, admin_notes: notes })}>
                                <SelectTrigger data-testid={`grv-status-${g.id}`} className="w-48"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button data-testid={`grv-save-notes-${g.id}`} onClick={() => patchGrievance(g.id, { admin_notes: notes })} variant="outline" className="rounded-full h-8">Save notes</Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
